import { Checkbox, Form, Input, InputNumber, Modal, Select } from "antd";
import { useEffect, useState } from "react";
import MaskedInput from "antd-mask-input";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { validaCPF, validateCelular } from "../../../components/Globais";
import { GerarCobrancaAssinaturaResponse } from "../../../services/cadastrarAssinatura";
import { useMessage } from "../../../uteis/mensagens/useMessage";
import { layout } from "../../ViewGerenciarPostagens/styles";
import { Assinatura } from "../interfaces/Assinatura";
import { useAdicionarAssinatura } from "../hooks/useAdicionarAssinatura";
import { ContainerAssinatura, TitleAssinatura } from "../styles";
import { formatDate } from "../utils/dateUtils";

type AssinaturaFormData = {
  nome?: string;
  celular?: string;
  cpf?: string;
  quantidadeCotas?: number;
  formaPagamento?: "PIX" | "CARTAO";
  enviarWhatsapp?: boolean;
};

type FetchAssinaturasFn = (
  options?:
    | boolean
    | {
        showLoading?: boolean;
        preserveOnEmpty?: boolean;
      }
) => Promise<Assinatura[] | undefined>;

type ModalAdicionarProps = {
  isModalOpen: boolean;
  handleFechar: () => void;
  fetch: FetchAssinaturasFn;
  onAssinaturaCriada: (assinatura: Assinatura) => void;
};

const normalizarCelularAssinatura = (value?: string) => {
  const digits = String(value || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  return `+${digits}`;
};

const montarAssinaturaCriada = (
  data: GerarCobrancaAssinaturaResponse,
  values: AssinaturaFormData
): Assinatura => ({
  key: data.assinatura.id,
  id: data.assinatura.id,
  pessoaId: data.assinatura.pessoaId ?? null,
  planoAssinatura: data.assinatura.planoAssinatura ?? "ANUNCIO_PROPRIEDADE",
  created_at: formatDate(new Date()),
  dataAssinatura: "",
  dataVencimento: "",
  cotasUsuariosId: data.assinatura.cotaUsuarioId ?? null,
  active: 1,
  status: data.assinatura.status ?? "PENDENTE",
  nomeUsuario: String(values.nome || "").trim(),
  nome: String(values.nome || "").trim(),
  celular: normalizarCelularAssinatura(values.celular),
  formaPagamento: values.formaPagamento,
  formaPagamentoPreferida: values.formaPagamento,
  numeroApto: "-",
  nomeEmpreendimento: "Sem cota vinculada",
});

export default function ModalAdicionar({
  isModalOpen,
  handleFechar,
  fetch,
  onAssinaturaCriada,
}: ModalAdicionarProps) {
  const [formAdicionarAssinatura] = Form.useForm();
  const [phone, setPhone] = useState("");
  const [errorDisplayed, setErrorDisplayed] = useState(false);

  const { showSuccess, showError, showWarning, contextHolder } = useMessage();

  const {
    isLoading: isLoadingAdicionar,
    error,
    cadastrar,
    resetError,
  } = useAdicionarAssinatura();

  const handlePhoneChange = (value: string) => {
    setPhone(value);

    const celular = normalizarCelularAssinatura(value);
    formAdicionarAssinatura.setFieldValue("celular", celular);
  };

  const handleCadastrar = async () => {
    try {
      const values = (await formAdicionarAssinatura.validateFields()) as
        AssinaturaFormData;
      const celular = normalizarCelularAssinatura(phone || values.celular);
      const erroCelular = validateCelular(celular);
      const cpf = String(values.cpf || "").replace(/\D/g, "");

      if (erroCelular) {
        showError(erroCelular);
        return;
      }

      if (!validaCPF(cpf)) {
        showError("Informe um CPF valido.");
        return;
      }

      const resultado = await cadastrar({
        nome: String(values.nome || "").trim(),
        celular,
        cpf,
        quantidadeCotas: Number(values.quantidadeCotas),
        formaPagamento: values.formaPagamento,
        planoAssinatura: "ANUNCIO_PROPRIEDADE",
        enviarWhatsapp: values.enviarWhatsapp ? true : undefined,
      });

      if (!resultado) {
        return;
      }

      if (resultado.variant === "warning") {
        showWarning(resultado.message);
      } else {
        showSuccess(resultado.message);
      }

      onAssinaturaCriada(montarAssinaturaCriada(resultado.data, values));
      formAdicionarAssinatura.resetFields();
      setPhone("");
      handleFechar();

      void fetch({ showLoading: false, preserveOnEmpty: true });
      window.setTimeout(() => {
        void fetch({ showLoading: false, preserveOnEmpty: true });
      }, 1500);
    } catch {
      return;
    }
  };

  useEffect(() => {
    if (error && !errorDisplayed) {
      showError(error);
      setErrorDisplayed(true);

      setTimeout(() => {
        setErrorDisplayed(false);
        resetError();
      }, 3000);
    }
  }, [
    showError,
    errorDisplayed,
    error,
    resetError,
  ]);

  useEffect(() => {
    if (isModalOpen) {
      resetError();
      setErrorDisplayed(false);
      formAdicionarAssinatura.resetFields();
      formAdicionarAssinatura.setFieldsValue({
        quantidadeCotas: 1,
        enviarWhatsapp: false,
      });
      setPhone("");
    }
  }, [isModalOpen, formAdicionarAssinatura, resetError]);

  return (
    <>
      {contextHolder}
      <Modal
        width={640}
        open={isModalOpen}
        onCancel={handleFechar}
        cancelText="Cancelar"
        okText="Cadastrar"
        onOk={handleCadastrar}
        confirmLoading={isLoadingAdicionar}
        okButtonProps={{
          style: {
            backgroundColor: "#003644",
            borderColor: "#003644",
            color: "white",
          },
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: "#f44336",
            borderColor: "#f44336",
            color: "white",
          },
        }}
      >
        <ContainerAssinatura>
          <TitleAssinatura>Adicionar Assinatura</TitleAssinatura>

          <Form
            {...layout}
            form={formAdicionarAssinatura}
            name="assinatura"
            style={{ marginTop: "24px" }}
            initialValues={{
              quantidadeCotas: 1,
              enviarWhatsapp: false,
            }}
          >
            <Form.Item
              name="nome"
              label="Nome Completo"
              className="formItems"
              rules={[{ required: true, message: "Informe o nome." }]}
            >
              <Input placeholder="Digite o nome" />
            </Form.Item>

            <Form.Item
              name="celular"
              label="Celular"
              className="formItems"
              rules={[{ required: true, message: "Informe o celular." }]}
            >
              <div style={{ width: "100%" }}>
                <PhoneInput
                  country={"br"}
                  value={phone}
                  onChange={handlePhoneChange}
                  enableSearch
                  placeholder="Digite o telefone"
                  inputStyle={{ width: "100%" }}
                  containerStyle={{ width: "100%" }}
                  dropdownStyle={{ zIndex: 2050 }}
                />
              </div>
            </Form.Item>

            <Form.Item
              name="cpf"
              label="CPF"
              className="formItems"
              rules={[
                { required: true, message: "Informe o CPF." },
                {
                  validator: async (_, value) => {
                    if (!value || validaCPF(value)) {
                      return;
                    }

                    throw new Error("Informe um CPF valido.");
                  },
                },
              ]}
            >
              <MaskedInput mask="000.000.000-00" placeholder="Digite o CPF" />
            </Form.Item>

            <Form.Item
              name="quantidadeCotas"
              label="Quantidade de cotas"
              className="formItems"
              rules={[
                { required: true, message: "Informe a quantidade de cotas." },
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: "100%" }}
                placeholder="Informe a quantidade de cotas"
              />
            </Form.Item>

            <Form.Item
              name="formaPagamento"
              label="Forma de pagamento"
              className="formItems"
              extra="Se nao selecionar, o link sera gerado com o meio de pagamento em aberto."
            >
              <Select
                allowClear
                placeholder="Selecione a forma de pagamento"
                options={[
                  { value: "CARTAO", label: "Cartao" },
                  { value: "PIX", label: "PIX" },
                ]}
              />
            </Form.Item>

            <Form.Item
              name="enviarWhatsapp"
              valuePropName="checked"
              className="formItems"
            >
              <Checkbox>Enviar Msg?</Checkbox>
            </Form.Item>
          </Form>
        </ContainerAssinatura>
      </Modal>
    </>
  );
}
